/**
 * OperatorTicketsList — every ticket placed at this storefront.
 *
 * Filters: state, status, placed_via (manual/scan). Click any row to open
 * the TicketDetail dialog; if the ticket has a scan_id, a "View original
 * scan" button fetches the persisted image from /api/operator/lottery/scans/{id}.
 *
 * Backed by GET /api/operator/lottery/tickets
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, RefreshCw, Loader2, Search, X, Flag, ShieldAlert } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { FLAG_REASON_LABELS } from '../constants/lotteryFlagReasons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const REASON_LABELS = FLAG_REASON_LABELS;

/** Lightweight in-browser "ping" using Web Audio API. No asset required.
 *  Plays a short 880 Hz sine when a new ticket arrives so cashiers don't
 *  miss it while looking elsewhere. Falls back to silent if the browser
 *  blocks audio autoplay (Chrome/Safari require user gesture first). */
function playNewTicketPing() {
    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.value = 0.0001;
        osc.connect(gain).connect(ctx.destination);
        const now = ctx.currentTime;
        gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        osc.start(now);
        osc.stop(now + 0.25);
        osc.onended = () => ctx.close();
    } catch { /* silent fallback */ }
}

const OperatorTicketsList = ({
    listEndpoint = '/operator/lottery/tickets',
    scanEndpoint = '/operator/lottery/scans',
    cashierFilter = null,             // [{ id, name }] — when present, show a "By cashier" filter
    pollMs = 20000,                   // auto-refresh interval, set 0 to disable
} = {}) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ state: '', status: '', placed_via: '', flagged: '', cashier_id: '', search: '' });
    const [selected, setSelected] = useState(null);
    // Track previously-seen ticket IDs across polls so we can surface a toast
    // when *new* ones land. We intentionally use a ref (not state) so the
    // diff check fires even if the user hasn't interacted between polls.
    const seenIdsRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter.state) params.set('state', filter.state);
            if (filter.status) params.set('status', filter.status);
            if (filter.placed_via) params.set('placed_via', filter.placed_via);
            if (filter.flagged) params.set('flagged', filter.flagged);
            if (filter.cashier_id) params.set('cashier_id', filter.cashier_id);
            params.set('limit', '200');
            const { data } = await axios.get(`${API}${listEndpoint}?${params.toString()}`,
                { withCredentials: true });
            const fresh = data.tickets || [];

            // Diff vs. the previous snapshot — toast + ping for each new ticket.
            // Skip on the very first load (seenIdsRef.current === null) so
            // we don't spam toasts for the initial roster.
            if (seenIdsRef.current !== null) {
                const prev = seenIdsRef.current;
                const newOnes = fresh.filter(t => !prev.has(t.id));
                if (newOnes.length > 0) {
                    playNewTicketPing();
                    const first = newOnes[0];
                    toast.success(
                        newOnes.length === 1
                            ? `New ticket: ${first.user_name} — ${first.state} P${first.pick_type} ${fmt(first.amount)}`
                            : `${newOnes.length} new tickets just placed`,
                        { duration: 5000 },
                    );
                    // Fire-and-forget 80% cap warning: for each new pending
                    // ticket, fetch its bucket cap status; if near_cap or
                    // full, surface an amber warning toast so the cashier
                    // can re-route customers off saturated numbers.
                    const seenBuckets = new Set();  // dedupe within this poll
                    newOnes.forEach(t => {
                        if (t.status !== 'pending') return;
                        const key = `${t.state}|${t.pick_type}|${(t.numbers || []).join('-')}|${t.draw_date}|${t.drawing_time}|${t.bet_type}`;
                        if (seenBuckets.has(key)) return;
                        seenBuckets.add(key);
                        const params = new URLSearchParams({
                            state: t.state,
                            pick_type: String(t.pick_type),
                            numbers: (t.numbers || []).join(','),
                            draw_date: t.draw_date,
                            drawing_time: t.drawing_time,
                            bet_type: t.bet_type,
                            is_big3: String(t.bet_type === 'big3'),
                        });
                        axios.get(`${API}/lottery/cap-status?${params.toString()}`, { withCredentials: true })
                            .then(({ data }) => {
                                if (data.full) {
                                    toast.warning(
                                        `${t.state} ${data.is_big3 ? 'Big 3' : `P${t.pick_type} ${t.bet_type}`} ${(t.numbers||[]).join('-')} is FULL · $${data.used} / $${data.cap} — no more bets accepted on this number for ${t.draw_date} ${t.drawing_time}.`,
                                        { duration: 8000 },
                                    );
                                } else if (data.near_cap) {
                                    toast.warning(
                                        `${t.state} ${data.is_big3 ? 'Big 3' : `P${t.pick_type} ${t.bet_type}`} ${(t.numbers||[]).join('-')} approaching cap · $${data.used} / $${data.cap} (${Math.round(data.pct_used * 100)}%). Only $${data.remaining} left for ${t.draw_date} ${t.drawing_time}.`,
                                        { duration: 7000 },
                                    );
                                }
                            })
                            .catch(() => {});
                    });
                }
            }
            seenIdsRef.current = new Set(fresh.map(t => t.id));

            setTickets(fresh);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [listEndpoint, filter.state, filter.status, filter.placed_via, filter.flagged, filter.cashier_id]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh (real-time-ish feel without WebSocket overhead). Pauses
    // when the dialog is open so the user doesn't lose their place.
    useEffect(() => {
        if (!pollMs || selected) return;
        const handle = setInterval(load, pollMs);
        return () => clearInterval(handle);
    }, [load, pollMs, selected]);

    const visible = tickets.filter(t => {
        if (!filter.search) return true;
        const q = filter.search.toLowerCase();
        return (
            (t.user_name || '').toLowerCase().includes(q) ||
            (t.user_phone || '').includes(q) ||
            (t.numbers || []).join('').includes(q)
        );
    });

    return (
        <div className="space-y-4" data-testid="operator-tickets-list">
            <div className="flex flex-wrap items-end gap-2">
                <Filter label="State" value={filter.state}
                    onChange={v => setFilter({ ...filter, state: v })}
                    options={[['', 'All'], ['IL', 'Illinois'], ['MS', 'Mississippi'], ['TN', 'Tennessee']]}
                    testKey="state" />
                <Filter label="Status" value={filter.status}
                    onChange={v => setFilter({ ...filter, status: v })}
                    options={[['', 'All'], ['pending', 'Pending'], ['won', 'Won'], ['lost', 'Lost']]}
                    testKey="status" />
                <Filter label="Source" value={filter.placed_via}
                    onChange={v => setFilter({ ...filter, placed_via: v })}
                    options={[['', 'All'], ['manual', 'Manual'], ['scan', 'Scanned']]}
                    testKey="placed_via" />
                <Filter label="Flagged" value={filter.flagged}
                    onChange={v => setFilter({ ...filter, flagged: v })}
                    options={[['', 'All'], ['true', 'Flagged only'], ['false', 'Hide flagged']]}
                    testKey="flagged" />
                {cashierFilter && cashierFilter.length > 0 && (
                    <Filter label="By cashier" value={filter.cashier_id}
                        onChange={v => setFilter({ ...filter, cashier_id: v })}
                        options={[['', 'All cashiers'], ...cashierFilter.map(c => [c.id, c.name])]}
                        testKey="cashier_id" />
                )}
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Search</label>
                    <div className="relative mt-1">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
                        <Input
                            placeholder="Name / phone / digits"
                            value={filter.search}
                            onChange={e => setFilter({ ...filter, search: e.target.value })}
                            className="h-8 pl-8 bg-[#0a0a0a] border-zinc-700 text-zinc-100"
                            data-testid="ticket-search" />
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={load}
                    className="h-8 border-zinc-700 bg-zinc-900 text-zinc-300"
                    data-testid="tickets-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">When</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Player</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Game</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Numbers</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Amount</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Status</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Source</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow className="border-zinc-800">
                                    <TableCell colSpan={7} className="text-center text-zinc-500 py-6">
                                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && visible.length === 0 && (
                                <TableRow className="border-zinc-800">
                                    <TableCell colSpan={7} className="text-center text-zinc-500 py-6 text-sm">
                                        No tickets match these filters.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && visible.map((t, i) => (
                                <TableRow key={t.id}
                                    onClick={() => setSelected(t)}
                                    className="border-zinc-800 hover:bg-zinc-900/50 cursor-pointer"
                                    data-testid={`ticket-row-${i}`}>
                                    <TableCell className="text-xs text-zinc-400 whitespace-nowrap">
                                        {new Date(t.created_at).toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-zinc-100">
                                        <div className="font-medium">{t.user_name}</div>
                                        {t.user_phone && <div className="text-[11px] text-zinc-500">{t.user_phone}</div>}
                                    </TableCell>
                                    <TableCell className="text-zinc-300 text-sm">
                                        {t.bet_type === 'big3'
                                            ? <span className="text-amber-300 font-semibold">Big 3 · {t.state}</span>
                                            : <span>{t.state} Pick {t.pick_type} <span className="text-zinc-500">· {t.bet_type}</span></span>}
                                        <div className="text-[11px] text-zinc-500">{t.draw_date} · {t.drawing_time}</div>
                                    </TableCell>
                                    <TableCell className="font-mono text-zinc-100 tracking-wider">{(t.numbers || []).join('-')}</TableCell>
                                    <TableCell className="text-right font-mono text-zinc-300">{fmt(t.amount)}</TableCell>
                                    <TableCell><StatusBadge status={t.status} payout={t.payout} /></TableCell>
                                    <TableCell>
                                        {t.flagged
                                            ? <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                                <Flag className="w-3 h-3 mr-1" />
                                                {t.flag_status === 'pending' ? 'flagged' : t.flag_status}
                                              </Badge>
                                            : t.placed_via === 'scan'
                                                ? <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                    <Camera className="w-3 h-3 mr-1" /> Scan
                                                  </Badge>
                                                : <span className="text-xs text-zinc-500">{t.placed_by_role ? `${t.placed_by_role}` : 'manual'}</span>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <TicketDetailDialog ticket={selected} onClose={() => setSelected(null)} onChanged={load} scanEndpoint={scanEndpoint} />
        </div>
    );
};

const Filter = ({ label, value, onChange, options, testKey }) => (
    <div className="min-w-[120px]">
        <label className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</label>
        <select value={value} onChange={e => onChange(e.target.value)}
            className="mt-1 h-8 w-full bg-[#0a0a0a] border border-zinc-700 rounded text-zinc-100 text-sm px-2"
            data-testid={`ticket-filter-${testKey}`}>
            {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
    </div>
);

const StatusBadge = ({ status, payout }) => {
    const map = {
        pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        won:     'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        lost:    'bg-rose-500/20 text-rose-300 border-rose-500/30',
    };
    return (
        <Badge className={`${map[status] || 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30'} border`}>
            {status}{status === 'won' && payout ? ` +${fmt(payout)}` : ''}
        </Badge>
    );
};

const TicketDetailDialog = ({ ticket, onClose, onChanged }) => {
    const [scan, setScan] = useState(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [flagging, setFlagging] = useState(false);
    const [showFlagForm, setShowFlagForm] = useState(false);
    const [flagReason, setFlagReason] = useState('suspicious_scan');
    const [flagNote, setFlagNote] = useState('');

    useEffect(() => {
        setScan(null);
        setScanError(null);
        if (!ticket?.scan_id) return;
        setScanLoading(true);
        axios.get(`${API}/operator/lottery/scans/${ticket.scan_id}`, { withCredentials: true })
            .then(({ data }) => setScan(data))
            .catch(err => setScanError(err.response?.data?.detail || 'Could not load scan'))
            .finally(() => setScanLoading(false));
    }, [ticket?.scan_id]);

    const submitFlag = async () => {
        setFlagging(true);
        try {
            await axios.post(`${API}/operator/lottery/tickets/${ticket.id}/flag`,
                { reason: flagReason, note: flagNote.trim() || null },
                { withCredentials: true });
            toast.success('Ticket flagged. Admin will review.');
            setShowFlagForm(false);
            setFlagNote('');
            onChanged && onChanged();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not flag');
        } finally {
            setFlagging(false);
        }
    };

    const removeFlag = async () => {
        setFlagging(true);
        try {
            await axios.delete(`${API}/operator/lottery/tickets/${ticket.id}/flag`,
                { withCredentials: true });
            toast.success('Flag removed');
            onChanged && onChanged();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not remove flag');
        } finally {
            setFlagging(false);
        }
    };

    if (!ticket) return null;

    return (
        <Dialog open={!!ticket} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="ticket-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center justify-between pr-6">
                        Ticket detail
                        <StatusBadge status={ticket.status} payout={ticket.payout} />
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <DetailRow label="Player" value={ticket.user_name} />
                    {ticket.user_phone && <DetailRow label="Phone" value={ticket.user_phone} />}
                    <DetailRow label="Game" value={ticket.bet_type === 'big3'
                        ? `Big 3 — ${ticket.state}`
                        : `${ticket.state} Pick ${ticket.pick_type} (${ticket.bet_type})`} />
                    <DetailRow label="Numbers" value={(ticket.numbers || []).join('-')} mono />
                    <DetailRow label="Drawing" value={`${ticket.draw_date} · ${ticket.drawing_time}`} />
                    {ticket.winning_numbers && (
                        <DetailRow label="Winning numbers" value={(ticket.winning_numbers || []).join('-')} mono />
                    )}
                    <DetailRow label="Amount" value={fmt(ticket.amount)} mono />
                    {ticket.payout > 0 && <DetailRow label="Payout" value={`+${fmt(ticket.payout)}`} mono />}
                    <DetailRow label="Placed by"
                        value={ticket.placed_by_role
                            ? `${ticket.placed_by_role}${ticket.placed_by_name ? ' · ' + ticket.placed_by_name : ''}`
                            : 'Player (self)'} />
                    <DetailRow label="Source" value={ticket.placed_via === 'scan' ? 'Photo scan' : 'Manual entry'} />
                    <DetailRow label="Placed at" value={new Date(ticket.created_at).toLocaleString()} />

                    {/* Flag section */}
                    <div className="pt-4 mt-2 border-t border-zinc-800" data-testid="ticket-flag-section">
                        {ticket.flagged ? (
                            <div className="bg-rose-950/30 border border-rose-500/30 rounded-md p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <ShieldAlert className="w-4 h-4 text-rose-300" />
                                    <span className="text-sm font-semibold text-rose-200">
                                        Flagged — {ticket.flag_status || 'pending'}
                                    </span>
                                </div>
                                <div className="text-xs text-zinc-300 space-y-1">
                                    <div><span className="text-zinc-500">Reason:</span> {REASON_LABELS[ticket.flag_reason] || ticket.flag_reason}</div>
                                    {ticket.flag_note && <div><span className="text-zinc-500">Note:</span> {ticket.flag_note}</div>}
                                    <div className="text-zinc-500">
                                        Flagged by {ticket.flagged_by_name} ({ticket.flagged_by_role})
                                        {ticket.flagged_at && ` · ${new Date(ticket.flagged_at).toLocaleString()}`}
                                    </div>
                                </div>
                                {ticket.flag_status === 'pending' && (
                                    <Button size="sm" variant="outline"
                                        onClick={removeFlag} disabled={flagging}
                                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                                        data-testid="ticket-unflag-btn">
                                        {flagging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />}
                                        Remove flag
                                    </Button>
                                )}
                            </div>
                        ) : showFlagForm ? (
                            <div className="bg-amber-950/20 border border-amber-500/30 rounded-md p-3 space-y-2"
                                data-testid="ticket-flag-form">
                                <p className="text-xs text-amber-200 font-semibold uppercase tracking-wider">
                                    Flag this ticket for admin review
                                </p>
                                <select value={flagReason}
                                    onChange={e => setFlagReason(e.target.value)}
                                    className="w-full h-9 bg-zinc-950 border border-zinc-700 rounded text-zinc-100 text-sm px-2"
                                    data-testid="flag-reason-select">
                                    {Object.entries(REASON_LABELS).map(([v, lbl]) =>
                                        <option key={v} value={v}>{lbl}</option>)}
                                </select>
                                <Input value={flagNote}
                                    onChange={e => setFlagNote(e.target.value)}
                                    placeholder="Optional note for the admin reviewer"
                                    className="bg-zinc-950 border-zinc-700 text-zinc-100"
                                    data-testid="flag-note-input" />
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={submitFlag} disabled={flagging}
                                        className="bg-rose-500 hover:bg-rose-400 text-white"
                                        data-testid="flag-submit-btn">
                                        {flagging ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Flag className="w-3 h-3 mr-1" />}
                                        Send to admin
                                    </Button>
                                    <Button size="sm" variant="outline"
                                        onClick={() => setShowFlagForm(false)}
                                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button size="sm" variant="outline"
                                onClick={() => setShowFlagForm(true)}
                                className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10"
                                data-testid="ticket-flag-btn">
                                <Flag className="w-3 h-3 mr-1.5" /> Flag as suspicious
                            </Button>
                        )}
                    </div>

                    {ticket.placed_via === 'scan' && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                            <h4 className="text-sm font-semibold text-zinc-100 mb-2 flex items-center gap-2">
                                <Camera className="w-4 h-4 text-purple-300" />
                                Original scan
                            </h4>
                            {scanLoading && (
                                <div className="text-zinc-500 text-sm py-4 text-center">
                                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                    Loading scan image…
                                </div>
                            )}
                            {scanError && (
                                <div className="text-rose-300 text-sm py-2 flex items-center gap-2">
                                    <X className="w-4 h-4" /> {scanError}
                                </div>
                            )}
                            {scan && (
                                <div className="space-y-3">
                                    <img
                                        src={`data:${scan.mime_type};base64,${scan.image_base64}`}
                                        alt="Scanned ticket"
                                        className="w-full max-h-[480px] object-contain rounded border border-zinc-700 bg-zinc-950"
                                        data-testid="scan-image" />
                                    <div className="text-[11px] text-zinc-500">
                                        Scanned by {scan.actor_name || 'Player'} ({scan.actor_role || 'user'}) on{' '}
                                        {new Date(scan.created_at).toLocaleString()}.
                                    </div>
                                    {scan.ocr_bets && scan.ocr_bets.length > 0 && (
                                        <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
                                            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">OCR parsed</p>
                                            <ul className="space-y-1 text-xs font-mono text-zinc-300">
                                                {scan.ocr_bets.map((b, i) => (
                                                    <li key={i}>
                                                        {b.game_type} {b.state} {b.drawing_time}: {(b.digits || []).join('-')}
                                                        {' '}× {fmt(b.amount)}
                                                        {b.bet_type ? ` (${b.bet_type})` : ''}
                                                        {typeof b.confidence === 'number' && b.confidence < 0.7 && (
                                                            <span className="text-amber-400"> · low confidence</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

const DetailRow = ({ label, value, mono }) => (
    <div className="flex justify-between text-sm gap-4">
        <span className="text-zinc-500 uppercase tracking-wider text-[11px] pt-0.5">{label}</span>
        <span className={`text-zinc-100 text-right ${mono ? 'font-mono tracking-wider' : ''}`}>{value}</span>
    </div>
);

export default OperatorTicketsList;
